import { useMemo, type ReactNode } from 'react';
import { Button, Card, CardSubtitle, CardTitle, Combobox, Field, Input, Select } from '@keobook/ui';
import {
  buildPresets,
  monthRange,
  monthsBetween,
  todayIso,
  yearRange,
  yearsBetween,
  type DateRange,
  type PeriodBounds,
} from '../lib/reportPeriods';

/**
 * Reusable date-range picker: presets + specific month/year quick-picks +
 * a custom from/to range. Extracted from Reports.tsx so the report comparison
 * view can render two independent instances (Source + Target). Fully
 * controlled — the parent owns the range.
 */
export function PeriodPicker({
  value,
  onChange,
  bounds,
  title = 'Period',
  subtitle,
  children,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
  bounds: PeriodBounds | null;
  title?: string;
  subtitle?: ReactNode;
  children?: ReactNode;
}) {
  const { from, to } = value;
  const presets = useMemo(() => buildPresets(bounds), [bounds]);
  const activePresetId = presets.find((p) => p.range.from === from && p.range.to === to)?.id;
  const invalidRange =
    from && to && from > to ? 'The "from" date must be on or before the "to" date.' : null;

  return (
    <Card>
      <CardTitle>{title}</CardTitle>
      {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: '0.875rem' }}>
        {presets.map((p) => (
          <Button
            key={p.id}
            size="sm"
            variant={activePresetId === p.id ? 'primary' : 'ghost'}
            aria-pressed={activePresetId === p.id}
            onClick={() => onChange(p.range)}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {bounds?.earliest && (
        <div
          style={{
            marginTop: '1.125rem',
            paddingTop: '1rem',
            borderTop: '1px solid var(--kb-gray-200)',
          }}
        >
          <div
            className="kb-muted"
            style={{ fontSize: 13, marginBottom: '0.5rem', fontWeight: 500 }}
          >
            Specific period
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Field
              label="Pick a month"
              hint="Type to filter — e.g. &ldquo;dec&rdquo; for every December."
            >
              <Combobox
                placeholder="— Select a month —"
                width={220}
                value={(() => {
                  if (!from || !to) return '';
                  const candidate = from.slice(0, 7);
                  const r = monthRange(candidate);
                  return r.from === from && r.to === to ? candidate : '';
                })()}
                options={monthsBetween(bounds.earliest, todayIso())}
                onChange={(v) => onChange(monthRange(v))}
              />
            </Field>
            <Field label="Pick a year">
              <Select
                value={(() => {
                  if (!from || !to) return '';
                  const y = from.slice(0, 4);
                  const r = yearRange(Number(y));
                  return r.from === from && r.to === to ? y : '';
                })()}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  if (!v) return;
                  onChange(yearRange(Number(v)));
                }}
              >
                <option value="">— Select a year —</option>
                {yearsBetween(bounds.earliest, todayIso()).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: '1.125rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--kb-gray-200)',
        }}
      >
        <div className="kb-muted" style={{ fontSize: 13, marginBottom: '0.5rem', fontWeight: 500 }}>
          Custom range
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="From">
            <Input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => onChange({ from: e.currentTarget.value, to })}
            />
          </Field>
          <Field label="To">
            <Input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => onChange({ from, to: e.currentTarget.value })}
            />
          </Field>
        </div>
      </div>

      {children}

      {invalidRange && (
        <div role="alert" className="kb-form-error" style={{ marginTop: 10 }}>
          {invalidRange}
        </div>
      )}
    </Card>
  );
}
